import React, { useState, useEffect } from 'react';
import { Table, Input, Button, Space, Tag, Typography, Card, message, Select, DatePicker, Row, Col, Modal, Form, Alert } from 'antd';
import { SearchOutlined, EyeOutlined, CopyOutlined, PlusOutlined } from '@ant-design/icons';
import { useSearchParams, useNavigate, Link } from 'react-router-dom';
import { apiClient, API_ENDPOINTS } from '../../../utils/api';
import styled from 'styled-components';
import dayjs from 'dayjs';

const COLORS = {
  bgPrimary: '#0D1117',
  bgSecondary: '#161B22',
  border: '#1f2a37',
  textPrimary: '#E2E8F0',
  textSecondary: '#94A3B8',
  accent: '#22D3EE',
};

const { Title } = Typography;
const { RangePicker } = DatePicker;

const PageContainer = styled.div`
  padding: 0;
  color: ${COLORS.textPrimary};
  
  @media (max-width: 768px) {
    padding: 0 8px;
  }

  /* Fix pagination text */
  .ant-pagination {
    color: ${COLORS.textSecondary};

    .ant-pagination-item {
      background-color: ${COLORS.bgSecondary};
      border-color: ${COLORS.border};

      a {
        color: ${COLORS.textPrimary};
      }

      &.ant-pagination-item-active {
        background-color: ${COLORS.accent};
        border-color: ${COLORS.accent};

        a {
          color: ${COLORS.bgPrimary};
        }
      }
    }

    .ant-pagination-prev,
    .ant-pagination-next {
      background-color: ${COLORS.bgSecondary};
      border-color: ${COLORS.border};

      .ant-pagination-item-link {
        color: ${COLORS.textPrimary};
        background-color: ${COLORS.bgSecondary};
        border-color: ${COLORS.border};
      }
    }

    .ant-pagination-options {
      .ant-select-selector {
        background-color: ${COLORS.bgSecondary} !important;
        color: ${COLORS.textPrimary} !important;
        border-color: ${COLORS.border} !important;
      }
      .ant-select-arrow {
        color: ${COLORS.textSecondary} !important;
      }
    }

    .ant-pagination-total-text {
      color: ${COLORS.textSecondary};
    }
  }
`;

const HeaderContainer = styled.div`
  margin-bottom: 24px;
  
  h2 {
    margin-bottom: 0 !important;
    font-size: 24px !important;
    font-weight: 600 !important;
  }
  
  @media (max-width: 768px) {
    margin-bottom: 16px;
    
    h2 {
      font-size: 20px !important;
    }
  }
`;

const FiltersContainer = styled(Card)`
  margin-bottom: 24px;
  background: ${COLORS.bgSecondary} !important;
  border: 1px solid ${COLORS.border} !important;
  
  .ant-card-body {
    padding: 20px !important;
  }
  
  @media (max-width: 768px) {
    margin-bottom: 16px;
    
    .ant-card-body {
      padding: 12px !important;
    }
  }
`;

const TableScrollWrapper = styled.div`
  @media (max-width: 768px) {
    overflow-x: auto;
    -webkit-overflow-scrolling: touch;
    margin: 0 -8px;
    padding: 0 8px;
  }
`;

const FilterRow = styled(Row)`
  margin-bottom: 16px;
  
  &:last-child {
    margin-bottom: 0;
  }
`;

const FilterLabel = styled.span`
  color: ${COLORS.textSecondary};
  margin-right: 8px;
  display: inline-block;
  min-width: 120px;
`;

const MerchantWallets: React.FC = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [wallets, setWallets] = useState<any[]>([]);
  const [pagination, setPagination] = useState({
    current: 1,
    pageSize: 10,
    total: 0,
  });
  const [assignModalVisible, setAssignModalVisible] = useState(false);
  const [assignForm] = Form.useForm();
  const [assigning, setAssigning] = useState(false);
  const [assignedWalletId, setAssignedWalletId] = useState<string | null>(null);

  // Filter states
  const [search, setSearch] = useState(searchParams.get('search') || '');
  const [memberIdFilter, setMemberIdFilter] = useState<string>(searchParams.get('externalMemberId') || '');
  const [walletStatusFilter, setWalletStatusFilter] = useState<string>(searchParams.get('walletStatus') || '');
  const [statusFilter, setStatusFilter] = useState<string>(searchParams.get('status') || '');
  const [hasMemberFilter, setHasMemberFilter] = useState<string>(searchParams.get('hasMember') || '');
  const [notionalBalanceFilter, setNotionalBalanceFilter] = useState<string>(searchParams.get('notionalBalance') || '');
  const [createdDateRange, setCreatedDateRange] = useState<any>(null);
  const [updatedDateRange, setUpdatedDateRange] = useState<any>(null);

  const pageId = parseInt(searchParams.get('pageId') || '1');
  const perPage = parseInt(searchParams.get('perPage') || '10');

  // Parse date ranges from URL params
  useEffect(() => {
    const dateFrom = searchParams.get('dateFrom');
    const dateTo = searchParams.get('dateTo');
    if (dateFrom && dateTo) {
      setCreatedDateRange([dayjs(dateFrom), dayjs(dateTo)]);
    }

    const updatedFrom = searchParams.get('updatedFrom');
    const updatedTo = searchParams.get('updatedTo');
    if (updatedFrom && updatedTo) {
      setUpdatedDateRange([dayjs(updatedFrom), dayjs(updatedTo)]);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    fetchWallets();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pageId, perPage, search, memberIdFilter, walletStatusFilter, statusFilter, hasMemberFilter, notionalBalanceFilter, createdDateRange, updatedDateRange]);

  const fetchWallets = async () => {
    try {
      setLoading(true);
      const params: any = {
        pageId,
        perPage,
      };

      if (search) {
        params.search = search;
      }
      if (memberIdFilter) {
        params.externalMemberId = memberIdFilter;
      }
      if (walletStatusFilter) {
        params.walletStatus = walletStatusFilter;
      }
      if (statusFilter) {
        params.status = statusFilter;
      }
      if (hasMemberFilter) {
        params.hasMember = hasMemberFilter;
      }
      if (notionalBalanceFilter) {
        params.notionalBalance = notionalBalanceFilter;
      }
      if (createdDateRange && createdDateRange[0] && createdDateRange[1]) {
        params.dateFrom = createdDateRange[0].format('YYYY-MM-DD');
        params.dateTo = createdDateRange[1].format('YYYY-MM-DD');
      }
      if (updatedDateRange && updatedDateRange[0] && updatedDateRange[1]) {
        params.updatedFrom = updatedDateRange[0].format('YYYY-MM-DD');
        params.updatedTo = updatedDateRange[1].format('YYYY-MM-DD');
      }

      const response = await apiClient.get(API_ENDPOINTS.MERCHANT_GET_WALLETS || '/api/Merchant/lobby/wallets', { params });
      
      if (response.data?.status === 'success' && response.data?.data) {
        const data = response.data.data;
        setWallets(data.itemsList || []);
        const paginator = data.paginator || data.pagination;
        setPagination({
          current: paginator?.currentPage || pageId,
          pageSize: paginator?.perPage || perPage,
          total: paginator?.itemCount || paginator?.totalCount || paginator?.total || 0,
        });
      }
    } catch (error: any) {
      console.error('Error fetching wallets:', error);
      message.error(error.response?.data?.message || 'Failed to fetch wallets');
    } finally {
      setLoading(false);
    }
  };

  const updateURLParams = (updates: Record<string, string | null>) => {
    const newParams = new URLSearchParams(searchParams);
    Object.entries(updates).forEach(([key, value]) => {
      if (value) {
        newParams.set(key, value);
      } else {
        newParams.delete(key);
      }
    });
    newParams.set('pageId', '1'); // Reset to first page when filters change
    setSearchParams(newParams);
  };

  const handleFilterChange = (key: string, value: string | null) => {
    updateURLParams({ [key]: value });
  };

  const handleDateRangeChange = (key: string, dates: any) => {
    if (key === 'created') {
      setCreatedDateRange(dates);
      updateURLParams({
        dateFrom: dates && dates[0] ? dayjs(dates[0]).format('YYYY-MM-DD') : null,
        dateTo: dates && dates[1] ? dayjs(dates[1]).format('YYYY-MM-DD') : null,
      });
    } else if (key === 'updated') {
      setUpdatedDateRange(dates);
      updateURLParams({
        updatedFrom: dates && dates[0] ? dayjs(dates[0]).format('YYYY-MM-DD') : null,
        updatedTo: dates && dates[1] ? dayjs(dates[1]).format('YYYY-MM-DD') : null,
      });
    }
  };

  const handleClearFilters = () => {
    setSearch('');
    setMemberIdFilter('');
    setWalletStatusFilter('');
    setStatusFilter('');
    setHasMemberFilter('');
    setNotionalBalanceFilter('');
    setCreatedDateRange(null);
    setUpdatedDateRange(null);
    setSearchParams(new URLSearchParams({ pageId: '1', perPage: '10' }));
  };

  const handleTableChange = (newPagination: any) => {
    const newParams = new URLSearchParams(searchParams);
    newParams.set('pageId', newPagination.current.toString());
    newParams.set('perPage', newPagination.pageSize.toString());
    setSearchParams(newParams);
  };

  const handleCopy = (text: string, label: string) => {
    navigator.clipboard.writeText(text);
    message.success(`${label} copied to clipboard`);
  };

  const columns = [
    {
      title: 'Wallet ID',
      dataIndex: 'walletId',
      key: 'walletId',
      render: (id: string, record: any) => id ? (
        <Space>
          <span style={{ fontFamily: 'monospace', fontSize: '12px' }}>
            {id.substring(0, 16)}...
          </span>
          <Button
            type="text"
            size="small"
            icon={<CopyOutlined />}
            onClick={() => handleCopy(id, 'Wallet ID')}
            style={{ color: COLORS.accent }}
          />
        </Space>
      ) : '-',
    },
    {
      title: 'Member',
      key: 'member',
      render: (_: any, record: any) => {
        const name = record.externalMemberName;
        const memberId = record.externalMemberId;
        if (!name && !memberId) {
          return <Tag color="default">Unassigned</Tag>;
        }
        return (
          <Space direction="vertical" size={0}>
            {name && <span style={{ fontWeight: 500 }}>{name}</span>}
            {memberId && (
              <span style={{ fontSize: '11px', color: COLORS.textSecondary, fontFamily: 'monospace' }}>
                {memberId.length > 20 ? `${memberId.substring(0, 20)}...` : memberId}
              </span>
            )}
          </Space>
        );
      },
    },
    {
      title: 'EVM Address',
      dataIndex: 'evmAddress',
      key: 'evmAddress',
      render: (address: string) => address ? (
        <Space>
          <span style={{ fontFamily: 'monospace', fontSize: '12px' }}>
            {address.substring(0, 10)}...{address.substring(address.length - 8)}
          </span>
          <Button
            type="text"
            size="small"
            icon={<CopyOutlined />}
            onClick={() => handleCopy(address, 'EVM Address')}
            style={{ color: COLORS.accent }}
          />
        </Space>
      ) : '-',
    },
    {
      title: 'Bitcoin Address',
      dataIndex: 'bitcoinAddress',
      key: 'bitcoinAddress',
      render: (address: string) => address ? (
        <Space>
          <span style={{ fontFamily: 'monospace', fontSize: '12px' }}>
            {address.substring(0, 10)}...{address.substring(address.length - 8)}
          </span>
          <Button
            type="text"
            size="small"
            icon={<CopyOutlined />}
            onClick={() => handleCopy(address, 'EVM Address')}
            style={{ color: COLORS.accent }}
          />
        </Space>
      ) : '-',
    },
    {
      title: 'Solana Address',
      dataIndex: 'solanaAddress',
      key: 'solanaAddress',
      render: (address: string) => address ? (
        <Space>
          <span style={{ fontFamily: 'monospace', fontSize: '12px' }}>
            {address.substring(0, 10)}...{address.substring(address.length - 8)}
          </span>
          <Button
            type="text"
            size="small"
            icon={<CopyOutlined />}
            onClick={() => handleCopy(address, 'Solana Address')}
            style={{ color: COLORS.accent }}
          />
        </Space>
      ) : '-',
    },
    {
      title: 'TRON Address',
      dataIndex: 'tronAddress',
      key: 'tronAddress',
      render: (address: string) => address ? (
        <Space>
          <span style={{ fontFamily: 'monospace', fontSize: '12px' }}>
            {address.substring(0, 10)}...{address.substring(address.length - 8)}
          </span>
          <Button
            type="text"
            size="small"
            icon={<CopyOutlined />}
            onClick={() => handleCopy(address, 'TRON Address')}
            style={{ color: COLORS.accent }}
          />
        </Space>
      ) : '-',
    },
    {
      title: 'Wallet Status',
      dataIndex: 'walletStatus',
      key: 'walletStatus',
      render: (status: string) => (
        <Tag color={status === 'ACTIVE' ? 'green' : status === 'PENDING' ? 'orange' : 'red'}>
          {status}
        </Tag>
      ),
    },
    {
      title: 'Status',
      dataIndex: 'status',
      key: 'status',
      render: (status: string) => (
        <Tag color={status === 'ACTIVE' ? 'green' : status === 'PENDING' ? 'orange' : 'red'}>
          {status}
        </Tag>
      ),
    },
    {
      title: 'Notional Balance',
      dataIndex: 'notionalBalance',
      key: 'notionalBalance',
      render: (balance: number) => {
        if (balance === null || balance === undefined) return '-';
        const numBalance = Number(balance);
        const color = numBalance > 0 ? 'green' : numBalance < 0 ? 'red' : 'gray';
        return <Tag color={color}>{numBalance.toLocaleString('en-US', { minimumFractionDigits: 6, maximumFractionDigits: 6 })}</Tag>;
      },
    },
    {
      title: 'Created',
      dataIndex: 'createdAt',
      key: 'createdAt',
      render: (date: string) => date ? dayjs(date).format('YYYY-MM-DD HH:mm') : '-',
    },
    {
      title: 'Actions',
      key: 'actions',
      render: (_: any, record: any) => (
        <Link
          to={`/igwena/wallets/${record.id}`}
          onClick={(e) => {
            e.stopPropagation();
            navigate(`/igwena/wallets/${record.id}`);
          }}
        >
          <Button
            icon={<EyeOutlined />}
            size="small"
          >
            View
          </Button>
        </Link>
      ),
    },
  ];

  const handleAssignWallet = async (values: any) => {
    try {
      setAssigning(true);
      const response = await apiClient.post(API_ENDPOINTS.MERCHANT_GENERATE_WALLET, {
        externalMemberId: values.externalMemberId,
        externalMemberName: values.externalMemberName,
        externalMemberEmail: values.externalMemberEmail,
      });

      if (response.data?.status === 'success' && response.data?.data) {
        message.success('Wallet assigned successfully');
        
        // Clear the form immediately
        assignForm.resetFields();
        
        // Refresh wallets list
        await fetchWallets();
        
        // Find the wallet by walletId or externalMemberId
        const walletId = response.data.data.walletId;
        let foundWalletId: string | null = null;
        
        // Try to find the wallet in the updated list
        try {
          const searchResponse = await apiClient.get(API_ENDPOINTS.MERCHANT_GET_WALLETS, {
            params: { externalMemberId: values.externalMemberId, pageId: 1, perPage: 10 },
          });
          if (searchResponse.data?.status === 'success' && searchResponse.data?.data) {
            const items = searchResponse.data.data.itemsList || [];
            const foundWallet = items.find((w: any) => 
              w.walletId === walletId || w.externalMemberId === values.externalMemberId
            );
            if (foundWallet) {
              foundWalletId = foundWallet.id;
            }
          }
        } catch (searchError) {
          console.error('Error searching for wallet:', searchError);
        }
        
        // Set the assigned wallet ID to show the link
        if (foundWalletId) {
          setAssignedWalletId(foundWalletId);
        }
      }
    } catch (error: any) {
      message.error(error.response?.data?.message || 'Failed to assign wallet');
    } finally {
      setAssigning(false);
    }
  };

  return (
    <PageContainer>
      <HeaderContainer>
        <Space style={{ width: '100%', justifyContent: 'space-between' }}>
          <Title level={2} style={{ color: COLORS.textPrimary, marginBottom: 0 }}>
            MPC Wallets
          </Title>
          <Button
            type="primary"
            icon={<PlusOutlined />}
            onClick={() => setAssignModalVisible(true)}
            style={{
              background: COLORS.accent,
              borderColor: COLORS.accent,
            }}
          >
            Assign Wallet to Member
          </Button>
        </Space>
      </HeaderContainer>

      <Alert
        message="Only wallets assigned to members (users) should be used for transactions. Funding unassigned wallets may not be discovered promptly."
        type="info"
        showIcon
        style={{ marginBottom: 16 }}
      />

      <FiltersContainer>
        <Space direction="vertical" style={{ width: '100%' }} size="middle">
          {/* Row 1: Search and Basic Filters */}
          <FilterRow gutter={[16, 16]}>
            <Col xs={24} sm={12} md={8} lg={6}>
              <FilterLabel>Search</FilterLabel>
              <Input
                placeholder="Wallet ID, addresses, member ID"
                prefix={<SearchOutlined />}
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                onPressEnter={() => handleFilterChange('search', search || null)}
                onBlur={() => handleFilterChange('search', search || null)}
                allowClear
              />
            </Col>
            <Col xs={24} sm={12} md={8} lg={6}>
              <FilterLabel>Member ID</FilterLabel>
              <Input
                placeholder="External member ID"
                value={memberIdFilter}
                onChange={(e) => setMemberIdFilter(e.target.value)}
                onPressEnter={() => handleFilterChange('externalMemberId', memberIdFilter || null)}
                onBlur={() => handleFilterChange('externalMemberId', memberIdFilter || null)}
                allowClear
              />
            </Col>
            <Col xs={24} sm={12} md={8} lg={6}>
              <FilterLabel>Has Member</FilterLabel>
              <Select
                placeholder="All"
                value={hasMemberFilter || undefined}
                onChange={(value) => {
                  setHasMemberFilter(value);
                  handleFilterChange('hasMember', value || null);
                }}
                allowClear
                style={{ width: '100%' }}
              >
                <Select.Option value="true">Has Member</Select.Option>
                <Select.Option value="false">No Member</Select.Option>
              </Select>
            </Col>
          </FilterRow>

          {/* Row 2: Status Filters */}
          <FilterRow gutter={[16, 16]}>
            <Col xs={24} sm={12} md={8} lg={6}>
              <FilterLabel>Wallet Status</FilterLabel>
              <Select
                placeholder="All statuses"
                value={walletStatusFilter || undefined}
                onChange={(value) => {
                  setWalletStatusFilter(value);
                  handleFilterChange('walletStatus', value || null);
                }}
                allowClear
                style={{ width: '100%' }}
              >
                <Select.Option value="ACTIVE">Active</Select.Option>
                <Select.Option value="PENDING">Pending</Select.Option>
                <Select.Option value="DELETED">Deleted</Select.Option>
              </Select>
            </Col>
            <Col xs={24} sm={12} md={8} lg={6}>
              <FilterLabel>Model Status</FilterLabel>
              <Select
                placeholder="All statuses"
                value={statusFilter || undefined}
                onChange={(value) => {
                  setStatusFilter(value);
                  handleFilterChange('status', value || null);
                }}
                allowClear
                style={{ width: '100%' }}
              >
                <Select.Option value="ACTIVE">Active</Select.Option>
                <Select.Option value="PENDING">Pending</Select.Option>
                <Select.Option value="DELETED">Deleted</Select.Option>
              </Select>
            </Col>
            <Col xs={24} sm={12} md={8} lg={6}>
              <FilterLabel>Notional Balance</FilterLabel>
              <Select
                placeholder="All balances"
                value={notionalBalanceFilter || undefined}
                onChange={(value) => {
                  setNotionalBalanceFilter(value);
                  handleFilterChange('notionalBalance', value || null);
                }}
                allowClear
                style={{ width: '100%' }}
              >
                <Select.Option value="negative">Negative</Select.Option>
                <Select.Option value="positive">Positive</Select.Option>
                <Select.Option value="zero">Zero</Select.Option>
              </Select>
            </Col>
            <Col xs={24} sm={12} md={8} lg={6}>
              <FilterLabel>Created Date</FilterLabel>
              <RangePicker
                value={createdDateRange}
                onChange={(dates) => handleDateRangeChange('created', dates)}
                format="YYYY-MM-DD"
                style={{ width: '100%' }}
                allowClear
              />
            </Col>
            <Col xs={24} sm={12} md={8} lg={6}>
              <FilterLabel>Updated Date</FilterLabel>
              <RangePicker
                value={updatedDateRange}
                onChange={(dates) => handleDateRangeChange('updated', dates)}
                format="YYYY-MM-DD"
                style={{ width: '100%' }}
                allowClear
              />
            </Col>
          </FilterRow>

          {/* Row 3: Clear Filters Button */}
          <FilterRow>
            <Col span={24}>
              <Button onClick={handleClearFilters} style={{ marginTop: 8 }}>
                Clear All Filters
              </Button>
            </Col>
          </FilterRow>
        </Space>
      </FiltersContainer>

      <Card
        style={{
          background: COLORS.bgSecondary,
          border: `1px solid ${COLORS.border}`,
        }}
      >
        <TableScrollWrapper>
          <Table
            columns={columns}
            dataSource={wallets}
            rowKey="id"
            loading={loading}
            pagination={{
              current: pagination.current,
              pageSize: pagination.pageSize,
              total: pagination.total,
              showSizeChanger: window.innerWidth > 768,
              showTotal: (total) => <span style={{ color: COLORS.textSecondary }}>Total {total} wallets</span>,
              pageSizeOptions: ['10', '20', '50', '100'],
            }}
            onChange={handleTableChange}
            style={{ background: 'transparent' }}
            size="small"
            scroll={{ x: 'max-content' }}
          />
        </TableScrollWrapper>
      </Card>

      <Modal
        title="Assign Wallet to Member"
        open={assignModalVisible}
        onOk={() => {
          if (assignedWalletId) {
            // If wallet was just assigned, close modal and navigate
            setAssignModalVisible(false);
            setAssignedWalletId(null);
            navigate(`/igwena/wallets/${assignedWalletId}`);
          } else {
            // Otherwise, submit the form
            assignForm.submit();
          }
        }}
        onCancel={() => {
          setAssignModalVisible(false);
          assignForm.resetFields();
          setAssignedWalletId(null);
        }}
        okText={assignedWalletId ? "View Wallet" : "Assign Wallet"}
        cancelText={assignedWalletId ? "Close" : "Cancel"}
        confirmLoading={assigning}
        okButtonProps={{
          style: {
            background: COLORS.accent,
            borderColor: COLORS.accent,
          },
        }}
        bodyStyle={{ background: COLORS.bgSecondary, color: COLORS.textPrimary }}
        style={{ top: 20 }}
      >
        {assignedWalletId ? (
          <div style={{ textAlign: 'center', padding: '20px 0' }}>
            <div style={{ marginBottom: 16 }}>
              <Tag color="green" style={{ fontSize: '14px', padding: '8px 16px' }}>
                ✓ Wallet assigned successfully!
              </Tag>
            </div>
            <div style={{ marginBottom: 16, color: COLORS.textSecondary }}>
              You can view the wallet details or assign another wallet to a member.
            </div>
            <Button
              type="link"
              onClick={() => {
                setAssignModalVisible(false);
                setAssignedWalletId(null);
                navigate(`/igwena/wallets/${assignedWalletId}`);
              }}
              style={{
                color: COLORS.accent,
                fontSize: '14px',
                padding: 0,
                height: 'auto',
              }}
            >
              View Wallet Details →
            </Button>
          </div>
        ) : (
          <Form
            form={assignForm}
            layout="vertical"
            onFinish={handleAssignWallet}
            style={{ marginTop: 20 }}
          >
            <Form.Item
              name="externalMemberId"
              label="External Member ID"
              rules={[{ required: true, message: 'Please enter external member ID' }]}
            >
              <Input
                placeholder="Unique identifier for the member in your system"
                style={{
                  background: COLORS.bgPrimary,
                  borderColor: COLORS.border,
                  color: COLORS.textPrimary,
                }}
              />
            </Form.Item>
            <Form.Item
              name="externalMemberName"
              label="Member Name"
              rules={[{ required: true, message: 'Please enter member name' }]}
            >
              <Input
                placeholder="Member full name"
                style={{
                  background: COLORS.bgPrimary,
                  borderColor: COLORS.border,
                  color: COLORS.textPrimary,
                }}
              />
            </Form.Item>
            <Form.Item
              name="externalMemberEmail"
              label="Member Email"
              rules={[
                { required: true, message: 'Please enter member email' },
                { type: 'email', message: 'Please enter a valid email' },
              ]}
            >
              <Input
                placeholder="member@example.com"
                style={{
                  background: COLORS.bgPrimary,
                  borderColor: COLORS.border,
                  color: COLORS.textPrimary,
                }}
              />
            </Form.Item>
          </Form>
        )}
      </Modal>
    </PageContainer>
  );
};

export default MerchantWallets;
